import { createStore, type StoreApi } from 'zustand/vanilla';

import { type MeasureKind } from '@/domain/construction';
import { type Vec2 } from '@/domain/math';
import {
  canRestructure,
  type EntityId,
  FULL_VIEW,
  type LessonView,
  type SceneDocument,
  type Style,
} from '@/domain/model';
import { type Scene } from '@/engine';
import {
  type ControllerView,
  type SolidPatch,
  type ToolController,
  type ToolPrompt,
} from '@/input';

/**
 * The view-model bridge between the controller and React.
 *
 * ## Why this exists at all
 *
 * Before it, every panel reached into `App`'s `useState` and each one grew its own wire
 * back to the controller. That works for two panels and rots at five: the controller
 * emits one view object, so *any* change re-renders *every* panel.
 *
 * The store fixes that by being the single subscriber and letting components select the
 * slice they actually read. A colour change re-renders the properties panel; it does not
 * re-render the layers list.
 *
 * ## What is deliberately NOT here
 *
 * `preview`, `snap` and the camera never enter the store. They change on every pointer
 * move, and routing them through React would mean a reconciliation pass per mouse event —
 * the 60 FPS budget does not survive that. They go straight from the controller to the
 * renderer, bypassing React entirely. This is invariant 2 in the layer README, and it is
 * the reason this file syncs a *subset* of `ControllerView` rather than all of it.
 *
 * ## Reference stability
 *
 * `sync` keeps the previous object when the new one is shallow-equal. Without it,
 * `style` — rebuilt on every emit — would be a fresh object each time and defeat every
 * selector downstream, re-rendering the properties panel on every frame of a drag. The
 * store is the right place for that repair: fixing it once here beats asking each
 * component to remember `useShallow`.
 */

/** Commands the panels issue. Stable identity, so selecting them never re-renders. */
export interface EditorCommands {
  readonly select: (ids: readonly EntityId[]) => void;
  readonly setActiveTool: (id: string) => void;
  readonly setStyle: (patch: Partial<Style>) => void;
  readonly setPresentation: (
    ids: readonly EntityId[],
    patch: {
      readonly visible?: boolean;
      readonly locked?: boolean;
      readonly label?: string | null;
      readonly isConstruction?: boolean;
    }
  ) => void;
  /** Lesson presentation: mode, scaffolding visibility, replay position. */
  readonly setLesson: (patch: Partial<LessonView>) => void;
  /** Shows or hides the two axes drawn through the origin behind the drawing. */
  readonly setShowGridAxes: (visible: boolean) => void;
  /** Adds a whole construction, built from written instructions. */
  readonly insertDocument: (document: SceneDocument) => void;
  /** Clipboard. Each reports whether it did anything, so a menu item can be greyed out. */
  readonly copy: () => boolean;
  readonly cut: () => boolean;
  readonly paste: () => boolean;
  readonly duplicate: () => boolean;
  /** Cuts the selection loose from what it was built on, leaving it where it is. */
  readonly detach: () => boolean;
  readonly remove: (ids: readonly EntityId[]) => void;
  readonly moveInDrawOrder: (id: EntityId, delta: number) => void;
  readonly setSolid: (ids: readonly EntityId[], patch: SolidPatch) => void;
  /** Where a point sits along its segment, as a fraction from the first end. */
  readonly setRatio: (ids: readonly EntityId[], t: number) => void;
  readonly setPlaneFree: (
    ids: readonly EntityId[],
    patch: { tilt?: number; size?: number }
  ) => void;
  readonly setPlaneTurn: (
    ids: readonly EntityId[],
    patch: { degrees?: number; reverse?: boolean }
  ) => void;
  readonly setAngleMark: (
    ids: readonly EntityId[],
    patch: { ticks?: number; showValue?: boolean; size?: number; valueOffset?: Vec2 }
  ) => void;
  /**
   * Fixes what an angle *is*, by constraining the figure rather than by rewriting the caption.
   *
   * `null` releases it. See `ToolController.setAngleValue` for why this is a constraint and not
   * an edit to the number.
   */
  readonly setAngleValue: (ids: readonly EntityId[], degrees: number | null) => boolean;
  /** Whether a mark's angle can be set at all — false when every one of its points is derived. */
  readonly canSetAngle: (id: EntityId) => boolean;
  /** Says which point of an angle the next number moves. A preference — it locks nothing. */
  readonly setAngleMover: (mark: EntityId, which: 'a' | 'vertex' | 'c') => void;
  /** The point of an angle the next number will move. */
  readonly angleMoverOf: (mark: EntityId) => EntityId | null;
  /** A mark's three points, whichever way its arms were given — a point, or the side itself. */
  readonly pointsOfAngle: (id: EntityId) => { a: EntityId; vertex: EntityId; c: EntityId } | null;
  /** The angles a sliding point can reach, or null when the mark has no such limit. */
  readonly angleReachOf: (id: EntityId) => { readonly low: number; readonly high: number } | null;
  /** The angle a mark is fixed at, in degrees, or null when it is free to be measured. */
  readonly fixedAngleOf: (id: EntityId) => number | null;
  /** Turns or resizes the selection about its own centre, by an exact amount. */
  readonly transformSelection: (patch: { rotation?: number; scale?: number }) => void;
  /** Draws `y = f(x)`. Returns whether the formula was usable. */
  readonly addFunctionGraph: (expression: string) => boolean;
  /** Edits a graph in place. Returns whether the change was usable. */
  readonly setFunctionGraph: (
    ids: readonly EntityId[],
    patch: { expression?: string; from?: number; to?: number; scale?: number }
  ) => boolean;
  /** Adds a bare pair of axes. Returns whether there was room for one. */
  readonly addAxes: () => boolean;
  /** Tabulates a graph. Returns whether the function has a variation table at all. */
  readonly addVariationTable: (graph: EntityId) => boolean;
  /** Changes how a pair of axes is drawn. */
  readonly setAxes: (
    ids: readonly EntityId[],
    patch: {
      extent?: number;
      extentY?: number;
      stepY?: number;
      scaleY?: number;
      center?: { readonly x: number; readonly y: number };
      step?: number;
      numbered?: boolean;
    }
  ) => boolean;
  /** Turns a drawn polygon into the base of a prism or a pyramid. Returns whether it could. */
  readonly solidFromPolygon: (polygon: EntityId, kind: 'prism' | 'pyramid') => boolean;
  /** Marks a graph's notable points, or changes which kinds are shown. */
  readonly setGraphMarks: (
    graph: EntityId,
    patch: { kinds?: readonly string[]; guides?: boolean }
  ) => void;
  /** Sizes the axes to a graph, by the rule that drew them in the first place. */
  readonly fitAxesTo: (graph: EntityId) => boolean;
  /** Empties the drawing, in one undo step. */
  readonly clearDocument: () => void;
  readonly addMeasurement: (kind: MeasureKind) => void;
  readonly undo: () => void;
  readonly redo: () => void;
}

export interface EditorStoreState {
  readonly document: SceneDocument;
  readonly scene: Scene;
  readonly selection: ReadonlySet<EntityId>;
  readonly activeToolId: string;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly undoLabel: string | null;
  readonly redoLabel: string | null;
  /** Fields the selection agrees on; an absent field means they differ. */
  readonly style: Partial<Style>;
  readonly styleAppliesToSelection: boolean;
  /** What the active tool is waiting for. */
  readonly prompt: ToolPrompt | null;
  /**
   * How the drawing is being presented.
   *
   * View state, not document state, and the only part of this store that is not a mirror of
   * the controller. It lives here because it is exactly what a view-model bridge is for: a
   * value several components read and none of them owns.
   */
  readonly lesson: LessonView;
  /**
   * Whether the backdrop's two axes are drawn.
   *
   * View state like `lesson`, and deliberately *not* part of `LessonView`: that describes which
   * entities of the document are shown, and the axes are not entities — they are ruling painted
   * behind everything by the grid layer. It is also not part of the document, so hiding them to
   * read a figure cannot follow a worksheet to whoever opens it next.
   */
  readonly showGridAxes: boolean;
  readonly commands: EditorCommands;
}

export type EditorStore = StoreApi<EditorStoreState>;

/**
 * Creates a store mirroring a controller, and wires the subscription.
 *
 * Returns an unsubscribe function alongside the store rather than leaving the
 * subscription dangling: a store outliving its controller would keep serving stale rows
 * to a panel that is still mounted, which is worse than an empty panel.
 */
export function createEditorStore(controller: ToolController): {
  readonly store: EditorStore;
  readonly dispose: () => void;
} {
  const commands: EditorCommands = {
    select: (ids) => {
      controller.select(ids);
    },
    setActiveTool: (id) => {
      controller.setActiveTool(id);
    },
    setStyle: (patch) => {
      controller.setStyle(patch);
    },
    setPresentation: (ids, patch) => {
      controller.setPresentation(ids, patch);
    },
    moveInDrawOrder: (id, delta) => {
      controller.moveInDrawOrder(id, delta);
    },
    setSolid: (ids, patch) => {
      controller.setSolid(ids, patch);
    },
    setPlaneFree: (ids, patch) => {
      controller.setPlaneFree(ids, patch);
    },
    setPlaneTurn: (ids, patch) => {
      controller.setPlaneTurn(ids, patch);
    },
    setRatio: (ids, t) => {
      controller.setRatio(ids, t);
    },
    transformSelection: (patch) => {
      controller.transformSelection(patch);
    },
    addFunctionGraph: (expression) => controller.addFunctionGraph(expression) !== null,
    setFunctionGraph: (ids, patch) => controller.setFunctionGraph(ids, patch),
    addAxes: () => controller.addAxes() !== null,
    addVariationTable: (graph) => controller.addVariationTable(graph) !== null,
    setAxes: (ids, patch) => controller.setAxes(ids, patch),
    fitAxesTo: (graph) => controller.fitAxesTo(graph),
    solidFromPolygon: (polygon, kind) => controller.solidFromPolygon(polygon, kind) !== null,
    setGraphMarks: (graph, patch) => {
      controller.setGraphMarks(graph, patch);
    },
    clearDocument: () => {
      controller.clearDocument();
    },
    setAngleMark: (ids, patch) => {
      controller.setAngleMark(ids, patch);
    },
    setAngleValue: (ids, degrees) => controller.setAngleValue(ids, degrees),
    canSetAngle: (id) => controller.canSetAngle(id),
    setAngleMover: (mark, which) => {
      controller.setAngleMover(mark, which);
    },
    angleMoverOf: (mark) => controller.angleMoverOf(mark),
    pointsOfAngle: (id) => controller.pointsOfAngle(id),
    angleReachOf: (id) => controller.reachOf(id),
    fixedAngleOf: (id) => controller.fixedAngleOf(id),
    setLesson: (patch) => {
      applyLesson({ ...store.getState().lesson, ...patch });
    },
    setShowGridAxes: (visible) => {
      store.setState({ showGridAxes: visible });
    },
    insertDocument: (incoming) => {
      controller.insertDocument(incoming);
    },
    copy: () => controller.copy(),
    cut: () => controller.cut(),
    paste: () => controller.paste(),
    duplicate: () => controller.duplicate(),
    detach: () => controller.detachSelection(),
    remove: (ids) => {
      controller.remove(ids);
    },
    addMeasurement: (kind) => {
      controller.addMeasurement(kind);
    },
    undo: () => {
      controller.undo();
    },
    redo: () => {
      controller.redo();
    },
  };

  const store = createStore<EditorStoreState>()(() => ({
    ...sliceOf(controller.view),
    lesson: FULL_VIEW,
    showGridAxes: true,
    commands,
  }));

  /*
   * Restructuring follows the mode. Enforced in the controller rather than by hiding
   * buttons, so a keyboard shortcut cannot walk through a door the UI merely stopped
   * drawing.
   */
  const applyLesson = (next: LessonView): void => {
    controller.setRestructuring(canRestructure(next.mode));
    store.setState({ lesson: next });
  };

  const dispose = controller.subscribe((view) => {
    store.setState((previous) => reconcile(previous, sliceOf(view)));
  });

  return { store, dispose };
}

type Slice = Omit<EditorStoreState, 'commands' | 'lesson' | 'showGridAxes'>;

function sliceOf(view: ControllerView): Slice {
  return {
    document: view.editor.document,
    scene: view.editor.scene,
    selection: view.editor.selection,
    activeToolId: view.activeToolId,
    canUndo: view.canUndo,
    canRedo: view.canRedo,
    undoLabel: view.undoLabel,
    redoLabel: view.redoLabel,
    style: view.style,
    styleAppliesToSelection: view.styleAppliesToSelection,
    prompt: view.prompt,
  };
}

/**
 * Keeps the previous `style` object when its contents are unchanged.
 *
 * Everything else in the slice is already reference-stable upstream — the document, scene
 * and selection are only rebuilt when they genuinely change. `style` is the exception
 * because the controller recomputes it from the selection on every emit.
 */
function reconcile(previous: EditorStoreState, next: Slice): Partial<EditorStoreState> {
  // `lesson` and `showGridAxes` are absent from the slice by construction, so the merge leaves
  // them alone: a controller emit must never reset the replay position, drop the class out of
  // presentation mode, or bring back axes the user just turned off.
  const merged: Slice = {
    ...next,
    ...(shallowEqual(previous.style, next.style) ? { style: previous.style } : {}),
    ...(samePrompt(previous.prompt, next.prompt) ? { prompt: previous.prompt } : {}),
  };
  return merged;
}

/**
 * Whether the tool prompt is unchanged.
 *
 * The controller rebuilds it on every emit, and a construction tool emits on every pointer
 * move — so without this the prompt strip re-renders once per mouse move while a tool is in
 * hand, which is exactly the cost this store exists to remove.
 */
function samePrompt(a: ToolPrompt | null, b: ToolPrompt | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  return (
    a.tool === b.tool &&
    a.step === b.step &&
    a.steps === b.steps &&
    a.hint === b.hint &&
    a.problem === b.problem
  );
}

function shallowEqual(a: Partial<Style>, b: Partial<Style>): boolean {
  const keys = Object.keys(a) as (keyof Style)[];
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((key) => a[key] === b[key]);
}
